<script setup lang="ts">
//#region imports
import { computed, onBeforeMount, ref } from 'vue';
import { useRoute } from 'vue-router';
import { I18N_SET_LANG } from '@/store/action_types/i18n';
import PopupManager from '@/components/PopupManager.vue';
import TheHeader from '@/components/TheHeader.vue';
import FloodModal from '@/components/FloodModal.vue';
import Notify from '@/components/Notify.vue';
import { useTypedStore } from '@/store';
import { bootstrapApp } from '@/setup/bootstrap';
import type { AvaliableLocales } from '@/i18n';
//#endregion

//#region composables
const store = useTypedStore();
const route = useRoute();
//#endregion

//#region state
const isLoad = ref(false);
//#endregion

//#region computed
const isAuthenticated = computed(() => store.getters['auth/isAuthenticated']);
const uuidRender = computed(() => store.state.other.uuidRender);
const showHeader = computed(() => !store.getters['nav/isAuthView']);
const ifRouteHide = computed(() => route.path !== '/login/auth');
//#endregion

//#region methods
function getLocalStorageLanguage() {
  const localStorageLanguage = localStorage.getItem('language');

  if (localStorageLanguage === null) {
    const defaultLanguage = global.theme.defaultLanguage;
    localStorage.setItem('language', defaultLanguage);

    return defaultLanguage;
  }

  return localStorageLanguage as AvaliableLocales;
}

async function bootUser() {
  try {
    await bootstrapApp();
    return true;
  } catch {
    return false;
  }
}
//#endregion

//#region hooks
onBeforeMount(async () => {
  const isBooted = isAuthenticated.value && await bootUser();

  if (!isBooted) {
    await store.dispatch(`i18n/${I18N_SET_LANG}`, getLocalStorageLanguage());
  }

  isLoad.value = true;
});
//#endregion
</script>

<template>
  <div
    id="app"
    :key="uuidRender"
  >
    <PopupManager ref="popupManager" />
    <Notify />
    <TheHeader
      v-if="showHeader && ifRouteHide"
    />
    <RouterView
      v-if="isLoad"
      class="view"
    />
    <!--    <VueProgressBar />-->
    <FloodModal />
  </div>
</template>

<style lang="scss">
@import "./assets/fonts/cerapro/stylesheet";


#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  font-weight: 400;
  width: 100%;
  min-width: 1140px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  color: #303634;
  height: 100vh;
  overflow-y: hidden;
  > div.view {
    overflow-y: auto;
  }
  > .reports.view {
    overflow-y: hidden;
  }
  .fas {
    font-family: 'Font Awesome 7 Pro';
    font-weight: 900;
  }
  .far {
    font-family: 'Font Awesome 7 Pro';
    font-weight: 400;
  }
  .fal {
    font-family: 'Font Awesome 7 Pro';
    font-weight: 300;
  }
  .view {
    flex-grow: 10;
  }
  .test_link {
    display: block;
    width: 12px;
    height: 12px;
    position: fixed;
    top: 0;
    left: 0;
    box-sizing: border-box;
  }
}
</style>
